import { createActionGroup, emptyProps, props } from '@ngrx/store';
import {
  FlexibilityPrices,
  RateCards,
  RateCardsParams,
  SupplierAllocation,
  SupplierAllocationsParams,
  SupplierAllocationsUpdate,
  SupplierBlend,
  SupplierBlendSave,
  SupplierGroupTemplate,
  SupplierGroupTemplateSave,
  SupplierManagedAllocationsUpdate,
} from '@ps/survey-utils';

export const SupplierActions = createActionGroup({
  source: 'Survey Supplier',
  events: {
    'Load Allocations': props<{
      surveyId: number;
      params: SupplierAllocationsParams;
    }>(),
    'Load Blends': props<{ locale: string }>(),
    'Load Group Templates': emptyProps(),
    'Load Flexibility Price Params': props<{ surveyId: number }>(),
    'Update Allocations': props<{
      surveyId: number;
      update: SupplierAllocationsUpdate;
    }>(),
    'Update Managed Allocations': props<{
      surveyId: number;
      update: SupplierManagedAllocationsUpdate;
    }>(),
    'Save Group As Template': props<{ data: SupplierGroupTemplateSave }>(),
    'Save Blend': props<{ data: SupplierBlendSave }>(),
    'Load Rate Cards': props<{ params: RateCardsParams }>(),
    'Reset Allocations': emptyProps(),
  },
});

export const SupplierApiActions = createActionGroup({
  source: 'Survey Supplier API',
  events: {
    'Load Allocations Success': props<{
      allocations: SupplierAllocation[];
    }>(),
    'Load Allocations Failure': props<{ error: string }>(),
    /**
     * Patches only each single supplier's `isActive` from a freshly fetched
     * allocations list (e.g. after an IR/LOI recalculation), leaving every other
     * field of the current allocations — including the user's local edits —
     * untouched. Distinct from `Load Allocations Success`, which replaces the
     * whole list.
     */
    'Sync Allocations Active State': props<{
      allocations: SupplierAllocation[];
    }>(),
    'Load Blends Success': props<{ blends: SupplierBlend[] }>(),
    'Load Blends Failure': props<{ error: string }>(),
    'Load Group Templates Success': props<{
      groupTemplates: SupplierGroupTemplate[];
    }>(),
    'Load Group Templates Failure': props<{ error: string }>(),
    'Load Flexibility Price Params Success': props<{
      params: FlexibilityPrices;
    }>(),
    'Load Flexibility Price Params Failure': props<{ error: string }>(),
    /**
     * Deliberately distinct from `Load Allocations Success` even though the PUT
     * returns the same shape: the survey-creation recalculation effect both
     * emits the load event and listens for this one, so sharing a single action
     * would make it retrigger itself.
     */
    'Update Allocations Success': props<{
      allocations: SupplierAllocation[];
      blendId?: string;
      // Present only on the managed Apply — mirrors the applied global price
      // override into the survey-creation suppliersMetaData (the PUT response
      // itself never echoes these back). Plain values: NgRx state/actions can't
      // carry Option.
      priceOverride?: number | null;
      manualOverride?: boolean;
      surveyPauseThreshold?: number | null;
      defaultPurePriceOverride?: boolean;
      isPurePriceRateCardEnable?: boolean;
      supplierDefaultAllocation?: boolean;
    }>(),
    'Update Allocations Failure': props<{ error: string }>(),
    'Save Group As Template Success': emptyProps(),
    'Save Group As Template Failure': props<{ error: string }>(),
    'Save Blend Success': props<{ blend: SupplierBlend }>(),
    'Save Blend Failure': props<{ error: string }>(),
    'Load Rate Cards Success': props<{
      rateCards: RateCards;
    }>(),
    'Load Rate Cards Failure': props<{ error: string }>(),
  },
});
